/**
 * E-posta altyapısı sözleşmesi (M5 WP 5.4; K-M5-40, K-M5-03/04/05). Belge: `docs/spec/castinflow/email.md`.
 *
 * - Tenant SMTP ayarı: parola yalnız yazılır (bir daha gösterilmez); bağlantı testi açık kullanıcı eylemidir; gönderim yalnız
 *   mevcut ayar başarıyla test edildiyse açılabilir.
 * - Şablonlar: kategori + izinli anahtar kelimeler (`{{anahtar}}`). Bilinmeyen ya da kategoride izinli olmayan anahtar kelime
 *   kaydı reddeder. Gövde HTML'i izin listesiyle temizlenir; metin alternatifi otomatik üretilir.
 * - Gönderim durumu: kuyrukta → gönderiliyor → SMTP sunucusu kabul etti | başarısız | sonucu belirsiz. SMTP kabulü alıcının
 *   okuduğu ya da kesin teslim edildiği anlamına gelmez; "sonucu belirsiz" otomatik tekrar edilmez (uzlaştırma gerekir).
 */
import { z } from 'zod';
const uuid = z.guid();
// ---------------------------------------------------------------------------------------------------------------------
// Temel değerler
// ---------------------------------------------------------------------------------------------------------------------
/** Başlık enjeksiyonu (CR/LF) ve denetim karakterleri reddedilir */
// eslint-disable-next-line no-control-regex
const NO_CONTROL = /^[^\u0000-\u001f\u007f]*$/;
export const EMAIL_ADDRESS_RE = /^[^@\s<>()[\]\\,;:"]+@[^@\s<>()[\]\\,;:"]+\.[^@\s<>()[\]\\,;:"]+$/;
export const emailAddressSchema = z
    .string()
    .trim()
    .min(3)
    .max(254)
    .regex(EMAIL_ADDRESS_RE, 'Geçerli bir e-posta adresi girin (ör. ad@firma.com.tr)');
export const emailDisplayNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(NO_CONTROL, 'Ad satır sonu ya da denetim karakteri içeremez');
export const emailRecipientSchema = z
    .object({
    address: emailAddressSchema,
    name: emailDisplayNameSchema.nullable().optional(),
})
    .strict();
/**
 * Tek gönderimde en fazla alıcı: Kime + Cc TOPLAMI (M6-H1). Tek tavan kuralı — veritabanı CHECK'i (migration 0076
 * `email_sends_recipient_total_ck`), bu sabit ve servis aynı sayıyı uygular. Cc eklemek tavanı yükseltmez.
 */
export const EMAIL_MAX_RECIPIENTS = 10;
/** Teklif e-postasının daha dar toplam alıcı sınırı (Kime + Cc); genel tavanı aşamaz. */
export const PROPOSAL_EMAIL_MAX_RECIPIENTS = 5;
/** Adres karşılaştırma anahtarı: baş/son boşluksuz ve küçük harf (aynı adresin iki yazımı tek alıcıdır). */
export function emailAddressKey(address) {
    return address.trim().toLowerCase();
}
/**
 * Alıcı kümesini tekilleştirir (M6-H1): aynı adres bir kez gider. İlk geçtiği yer korunur; adres hem Kime hem Cc
 * içindeyse Kime'de kalır ve Cc'den çıkarılır (bir alıcı iki kez zarf alıcısı olmaz, başlıkta iki kez görünmez).
 */
export function normalizeEmailRecipients(to, cc = []) {
    const seen = new Set();
    const pick = (list) => {
        const out = [];
        for (const r of list) {
            const k = emailAddressKey(r.address);
            if (seen.has(k))
                continue;
            seen.add(k);
            out.push(r);
        }
        return out;
    };
    const toOut = pick(to);
    const ccOut = pick(cc);
    return {
        to: toOut,
        cc: ccOut,
        total: toOut.length + ccOut.length,
        dropped: to.length + cc.length - toOut.length - ccOut.length,
    };
}
/** Serbest metinden adres listesi: virgül, noktalı virgül ya da boşlukla ayrılmış (arayüz alanları). */
export function splitEmailAddresses(text) {
    return text
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter((x) => x !== '');
}
/**
 * Kime + Cc adres listelerinin arayüz ve sunucuda AYNI kuralla denetimi: en az bir Kime, her adres geçerli biçimde,
 * tekilleştirme sonrası toplam `max`'ı aşmaz. `null` = geçerli.
 */
export function emailRecipientIssue(to, cc, max = EMAIL_MAX_RECIPIENTS) {
    if ([...to, ...cc].some((a) => !EMAIL_ADDRESS_RE.test(a.trim()) || a.trim().length > 254))
        return 'invalid_address';
    const n = normalizeEmailRecipients(to.map((address) => ({ address })), cc.map((address) => ({ address })));
    if (n.to.length === 0)
        return 'to_required';
    if (n.total > max)
        return 'too_many';
    return null;
}
export const EMAIL_TLS_MODES = ['starttls', 'implicit'];
export const EMAIL_TLS_MODE_LABELS = {
    starttls: 'STARTTLS (genellikle 587)',
    implicit: 'Doğrudan TLS (genellikle 465)',
};
export const EMAIL_TEMPLATE_CATEGORIES = [
    'general',
    'proposal',
    'lead',
    'ticket',
    'contact',
    'company',
];
export const EMAIL_CATEGORY_LABELS = {
    general: 'Genel',
    proposal: 'Teklif',
    lead: 'Müşteri adayı',
    ticket: 'Destek talebi',
    contact: 'Kişi',
    company: 'Şirket',
};
/** Kategori → gönderimde gereken kayıt tipi (`general` her kayıtla ya da kayıtsız kullanılabilir) */
export const EMAIL_CATEGORY_RECORD_TYPE = {
    general: null,
    proposal: 'proposal',
    lead: 'lead',
    ticket: 'ticket',
    contact: 'contact',
    company: 'company',
};
export const EMAIL_SEND_SOURCES = [
    'automation',
    'proposal',
    'sequence',
    'ticket',
    'manual',
    'test',
    // v3.1 S10 (E2): ekip gelen kutusundan e-posta kanalı yanıtı (kanal adresinden, zincir başlıklarıyla)
    'conversation',
];
export const EMAIL_SOURCE_LABELS = {
    automation: 'Otomasyon',
    proposal: 'Teklif',
    sequence: 'Aday sekansı',
    ticket: 'Destek yanıtı',
    manual: 'Elle',
    test: 'Deneme',
    conversation: 'Gelen kutusu yanıtı',
};
/**
 * Gönderim durumu (K-M5-05). `accepted` = SMTP sunucusu iletiyi kabul etti (2xx) — okundu/teslim edildi DEĞİLDİR.
 * `suppressed` = dış etki kapalı çalışma alanında (demo) iş kuyruğa girmedi.
 */
export const EMAIL_SEND_STATUSES = [
    'queued',
    'sending',
    'accepted',
    'failed',
    'unknown',
    'suppressed',
];
export const EMAIL_STATUS_LABELS = {
    queued: 'Kuyrukta',
    sending: 'Gönderiliyor',
    accepted: 'SMTP sunucusu kabul etti',
    failed: 'Başarısız',
    unknown: 'Sonucu belirsiz',
    suppressed: 'Gönderilmedi (dış etki kapalı)',
};
export const EMAIL_STATUS_HINTS = {
    queued: 'İleti gönderim kuyruğunda bekliyor.',
    sending: 'İleti şu anda SMTP sunucusuna iletiliyor.',
    accepted: 'SMTP sunucusu iletiyi kabul etti. Bu, alıcının iletiyi aldığı ya da okuduğu anlamına gelmez.',
    failed: 'İleti gönderilemedi; sunucu reddetti ya da bağlantı ileti aktarılmadan önce kurulamadı. Yeniden denenebilir.',
    unknown: 'Bağlantı, ileti aktarıldıktan sonra ve sunucu yanıtı gelmeden koptu. İleti alıcıya ulaşmış olabilir; otomatik tekrar gönderilmez, uzlaştırma gerekir.',
    suppressed: 'Bu çalışma alanında dış etkiler kapalıdır; ileti gönderilmedi.',
};
export const EMAIL_ERROR_CLASSES = [
    'config',
    'disabled',
    'guard_rejected',
    'dns',
    'connect',
    'tls',
    'auth',
    'sender_rejected',
    'recipient_rejected',
    'message_rejected',
    'timeout',
    'connection_lost',
    'internal',
    'attachment',
];
export const EMAIL_ERROR_LABELS = {
    config: 'SMTP ayarı eksik ya da geçersiz',
    disabled: 'E-posta gönderimi kapalı',
    guard_rejected: 'Hedef sunucu adresine izin verilmedi (özel/yerel ağ ya da izinsiz port)',
    dns: 'Sunucu adı çözülemedi',
    connect: 'Sunucuya bağlanılamadı',
    tls: 'TLS bağlantısı kurulamadı ya da sertifika doğrulanamadı',
    auth: 'Kimlik doğrulama başarısız',
    sender_rejected: 'Sunucu gönderici adresini reddetti',
    recipient_rejected: 'Sunucu alıcı adresini reddetti',
    message_rejected: 'Sunucu iletiyi reddetti',
    timeout: 'Sunucu zamanında yanıt vermedi',
    connection_lost: 'Bağlantı, ileti aktarıldıktan sonra koptu',
    internal: 'Beklenmeyen hata',
    attachment: 'PDF eki doğrulanamadı; ileti sunucuya gönderilmedi',
};
// ---------------------------------------------------------------------------------------------------------------------
// Ekler (teklif PDF'i)
// ---------------------------------------------------------------------------------------------------------------------
/**
 * Teklif PDF eki üst sınırı (bayt): 5 MiB. Ek base64 ile kodlanınca ~×1,37 büyür (≈ 7,2 MB); yaygın SMTP ileti sınırı olan
 * ~10 MB'ın (ör. Postfix varsayılanı 10 240 000 bayt) altında gövde ve başlıklara pay kalır. Sınırı aşan PDF eklenmez; gönderim
 * açık gerekçeyle reddedilir ve "gönderildi" sayılmaz (kuyruğa alınırken ve gönderim anında iki kez denetlenir).
 */
export const EMAIL_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
/** Bayt boyutu (tr-TR): 512 B · 12,4 KB · 3,1 MB */
export function formatByteSize(n) {
    if (n < 1024)
        return `${String(n)} B`;
    const kb = n / 1024;
    if (kb < 1024)
        return `${kb.toFixed(1).replace('.', ',')} KB`;
    return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
}
export const EMAIL_ATTACHMENT_KINDS = ['proposal_pdf'];
/** Gönderim kaydında sabitlenmiş ek: revizyon + revizyon içerik özeti kayıtta; PDF'in kendisi özel depoda (bağlantı yok). */
export const emailAttachmentSchema = z.object({
    kind: z.enum(EMAIL_ATTACHMENT_KINDS),
    fileName: z.string(),
    contentType: z.literal('application/pdf'),
    byteSize: z.number().int(),
    /** Gönderilen (kilitli) teklif revizyonu */
    revisionNo: z.number().int(),
    /** PDF içeriğinin SHA-256 özeti (gönderimde yeniden hesaplanıp karşılaştırılır) */
    sha256: z.string(),
});
/** Gönderim neden yapılmadı (`EmailEnqueuer` → `disabled`) */
export const EMAIL_DISABLED_REASONS = [
    'smtp_not_configured',
    'smtp_disabled',
    'demo_workspace',
    'hourly_limit',
    'daily_limit',
];
export const EMAIL_DISABLED_MESSAGES = {
    smtp_not_configured: 'E-posta gönderilmedi: bu çalışma alanında SMTP sunucusu tanımlı değil (Yönetim › E-posta).',
    smtp_disabled: 'E-posta gönderilmedi: SMTP gönderimi kapalı. Bağlantı testi başarılı olduktan sonra yönetici gönderimi açabilir (Yönetim › E-posta).',
    demo_workspace: 'E-posta gönderilmedi: demo çalışma alanında dış etkiler kapalıdır.',
    hourly_limit: 'E-posta gönderilmedi: çalışma alanının saatlik gönderim sınırı doldu.',
    daily_limit: 'E-posta gönderilmedi: çalışma alanının günlük gönderim sınırı doldu.',
};
/** Bağlantı testi / gönderim aşamaları */
export const EMAIL_TEST_STAGES = [
    'config',
    'guard',
    'connect',
    'tls',
    'auth',
    'send',
    'done',
];
export const EMAIL_VARIABLES = [
    {
        key: 'calisma_alani.ad',
        label: 'Çalışma alanı adı',
        categories: 'all',
        source: 'system',
        sample: 'Örnek Teknoloji A.Ş.',
        description: 'Şirket bilgilerindeki çalışma alanı adı.',
    },
    {
        key: 'gonderen.ad',
        label: 'Gönderen adı',
        categories: 'all',
        source: 'system',
        sample: 'Ayşe Demir',
        description: 'Gönderimi başlatan kullanıcının adı; kullanıcı yoksa (otomasyon) SMTP gönderici adı.',
    },
    {
        key: 'bugun',
        label: 'Bugünün tarihi',
        categories: 'all',
        source: 'system',
        sample: '29.09.2026',
        description: 'Çalışma alanı saat dilimine göre gönderim anındaki tarih (GG.AA.YYYY).',
    },
    {
        key: 'kayit.no',
        label: 'Kayıt numarası',
        categories: 'all',
        source: 'record',
        sample: '42',
        description: 'Gönderimin bağlı olduğu kaydın numarası (kayıt gerekir).',
    },
    {
        key: 'kayit.baslik',
        label: 'Kayıt başlığı',
        categories: 'all',
        source: 'record',
        sample: 'Örnek kayıt',
        description: 'Gönderimin bağlı olduğu kaydın başlığı (kayıt gerekir).',
    },
    {
        key: 'sorumlu.ad',
        label: 'Kayıt sorumlusu',
        categories: 'all',
        source: 'record',
        sample: 'Mehmet Kaya',
        description: 'Kaydın sorumlu kullanıcısının adı (kayıt gerekir).',
    },
    {
        key: 'baglanti.kayit',
        label: 'Kayıt bağlantısı (uygulama içi)',
        categories: 'all',
        source: 'link',
        sample: 'https://uygulama.example/l/ornek',
        description: 'Kaydı Castinflow içinde açan imzalı bağlantı. 30 gün geçerlidir; açan kişinin oturum açması ve kaydı görme yetkisi gerekir (yeni yetki vermez).',
    },
    {
        key: 'teklif.no',
        label: 'Teklif numarası',
        categories: ['proposal'],
        source: 'record',
        sample: 'TKL-00042',
        description: 'Teklifin belge numarası.',
    },
    {
        key: 'teklif.konu',
        label: 'Teklif konusu',
        categories: ['proposal'],
        source: 'record',
        sample: 'Yıllık bakım hizmeti',
        description: 'Teklifin final revizyonundaki konu.',
    },
    {
        key: 'teklif.toplam',
        label: 'Teklif toplamı',
        categories: ['proposal'],
        source: 'record',
        sample: '12.500,00 TRY',
        description: 'Final revizyonun genel toplamı (para birimiyle).',
    },
    {
        key: 'teklif.gecerlilik',
        label: 'Teklif geçerlilik tarihi',
        categories: ['proposal'],
        source: 'record',
        sample: '31.10.2026',
        description: 'Teklifin geçerlilik tarihi; tanımsızsa "belirtilmedi".',
    },
    {
        key: 'musteri.ad',
        label: 'Müşteri adı',
        categories: ['proposal'],
        source: 'record',
        sample: 'Örnek Müşteri Ltd.',
        description: 'Teklifin müşterisi (şirket ya da kişi) — final revizyonun anlık görüntüsünden.',
    },
    {
        key: 'baglanti.teklif',
        label: 'Teklif müşteri bağlantısı',
        categories: ['proposal'],
        source: 'link',
        secret: true,
        sample: 'https://formlar.example/t/#ornek',
        description: 'Müşterinin teklifi görüntüleyip kabul/ret yanıtı verdiği bağlantı (M3 teklif bağlantısı). Yalnız genel yayın ve çalışma alanı ayarı açıksa kullanılabilir; gönderimde yeni bağlantı üretilir ve önceki bağlantı iptal olur.',
    },
    {
        key: 'aday.ad',
        label: 'Aday adı',
        categories: ['lead'],
        source: 'record',
        sample: 'Can Öztürk',
        description: 'Müşteri adayının adı soyadı; yoksa şirket adı.',
    },
    {
        key: 'aday.sirket',
        label: 'Aday şirketi',
        categories: ['lead'],
        source: 'record',
        sample: 'Öztürk Makine',
        description: 'Müşteri adayının şirket adı.',
    },
    {
        key: 'adim.ad',
        label: 'Sekans adımı adı',
        categories: ['lead'],
        source: 'caller',
        sample: 'Tanışma e-postası',
        description: 'Aday sekansındaki e-posta adımının adı (yalnız sekans gönderiminde).',
    },
    {
        key: 'adim.metin',
        label: 'Sekans adımı metni',
        categories: ['lead'],
        source: 'caller',
        multiline: true,
        sample: 'Görüşmemiz için teşekkür ederiz. Önümüzdeki hafta sizi aramak isteriz.',
        description: 'Sekans ayarlarında adıma yazılan e-posta metni (yalnız sekans gönderiminde).',
    },
    {
        key: 'talep.no',
        label: 'Destek talebi numarası',
        categories: ['ticket'],
        source: 'record',
        sample: '128',
        description: 'Destek talebinin numarası.',
    },
    {
        key: 'talep.konu',
        label: 'Destek talebi konusu',
        categories: ['ticket'],
        source: 'record',
        sample: 'Fatura adresi güncellemesi',
        description: 'Destek talebinin konusu.',
    },
    {
        key: 'talep.sahibi',
        label: 'Talep sahibi',
        categories: ['ticket'],
        source: 'record',
        sample: 'Zeynep Arslan',
        description: 'Talep sahibinin adı.',
    },
    {
        key: 'mesaj.metin',
        label: 'Yanıt metni',
        categories: ['ticket'],
        source: 'caller',
        multiline: true,
        sample: 'Fatura adresiniz güncellendi. Başka bir konuda yardımcı olabilir miyiz?',
        description: 'Talep sahibine görünür yanıtın metni (yalnız destek yanıtı gönderiminde).',
    },
    {
        key: 'kisi.ad',
        label: 'Kişi adı',
        categories: ['contact'],
        source: 'record',
        sample: 'Elif Şahin',
        description: 'Kişinin adı soyadı.',
    },
    {
        key: 'sirket.ad',
        label: 'Şirket adı',
        categories: ['company'],
        source: 'record',
        sample: 'Şahin Lojistik',
        description: 'Şirketin adı.',
    },
];
export const EMAIL_VARIABLE_BY_KEY = new Map(EMAIL_VARIABLES.map((v) => [v.key, v]));
export function variableAllowedIn(def, category) {
    return def.categories === 'all' || def.categories.includes(category);
}
/** Kategoride kullanılabilen anahtar kelimeler (editör listesi) */
export function variablesForCategory(category) {
    return EMAIL_VARIABLES.filter((v) => variableAllowedIn(v, category));
}
/** `{{ anahtar }}` biçimi; anahtar küçük harf, rakam, alt çizgi ve nokta */
export const EMAIL_PLACEHOLDER_RE = /\{\{\s*([^{}]*?)\s*\}\}/g;
const VARIABLE_KEY_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;
/** Metindeki anahtar kelimeleri sınıflandırır (sıra korunur, tekrarlar teklenir). */
export function scanPlaceholders(text, category) {
    const used = new Set();
    const unknown = new Set();
    const notAllowed = new Set();
    for (const m of text.matchAll(EMAIL_PLACEHOLDER_RE)) {
        const key = (m[1] ?? '').trim();
        const def = VARIABLE_KEY_RE.test(key) ? EMAIL_VARIABLE_BY_KEY.get(key) : undefined;
        if (def === undefined)
            unknown.add(key === '' ? '(boş)' : key);
        else if (!variableAllowedIn(def, category))
            notAllowed.add(key);
        else
            used.add(key);
    }
    return { used: [...used], unknown: [...unknown], notAllowed: [...notAllowed] };
}
const SIGNATURE = '<p>Saygılarımızla,<br>{{gonderen.ad}}<br>{{calisma_alani.ad}}</p>';
export const DEFAULT_EMAIL_TEMPLATES = [
    {
        key: 'genel-bilgilendirme',
        category: 'general',
        name: 'Genel bilgilendirme',
        subject: '{{calisma_alani.ad}} bilgilendirme',
        bodyHtml: `<p>Merhaba,</p>\n<p>Bu ileti {{calisma_alani.ad}} adına gönderilmiştir.</p>\n${SIGNATURE}`,
        description: 'Serbest içerikli genel ileti için başlangıç şablonu.',
    },
    {
        key: 'kayit-bildirimi',
        category: 'general',
        name: 'Kayıt bildirimi (iç kullanıcı)',
        subject: '{{kayit.baslik}} kaydı hakkında',
        bodyHtml: '<p>Merhaba,</p>\n<p><strong>{{kayit.baslik}}</strong> (no: {{kayit.no}}) kaydıyla ilgili bir gelişme var. Kaydın sorumlusu: {{sorumlu.ad}}.</p>\n<p><a href="{{baglanti.kayit}}">Kaydı Castinflow’da aç</a> — bağlantıyı açmak için oturum açmanız ve kaydı görme yetkiniz gerekir.</p>\n<p>{{gonderen.ad}}</p>',
        description: 'Ekip içi bildirim (ör. otomasyon). Uygulama içi kayıt bağlantısı içerir; müşteriye gönderilmez.',
    },
    {
        key: 'teklif-gonderimi',
        category: 'proposal',
        name: 'Teklif özeti',
        subject: '{{teklif.no}} numaralı teklifimiz: {{teklif.konu}}',
        bodyHtml: `<p>Sayın {{musteri.ad}},</p>\n<p>{{teklif.konu}} konulu, {{teklif.no}} numaralı teklifimizin özeti aşağıdadır.</p>\n<ul>\n<li>Teklif no: {{teklif.no}}</li>\n<li>Toplam: {{teklif.toplam}}</li>\n<li>Geçerlilik: {{teklif.gecerlilik}}</li>\n</ul>\n<p>Sorularınız için bu iletiyi yanıtlayabilirsiniz.</p>\n${SIGNATURE}`,
        description: 'Teklif özetini (numara, toplam, geçerlilik) gönderir; bağlantı içermez.',
    },
    {
        key: 'teklif-baglantisi',
        category: 'proposal',
        name: 'Teklif bağlantısı',
        subject: '{{teklif.no}} numaralı teklifimizi inceleyin',
        bodyHtml: `<p>Sayın {{musteri.ad}},</p>\n<p>{{teklif.konu}} konulu teklifimizi aşağıdaki bağlantıdan görüntüleyebilir, PDF olarak indirebilir ve kabul ya da ret yanıtınızı iletebilirsiniz.</p>\n<p><a href="{{baglanti.teklif}}">Teklifi görüntüle</a></p>\n<p>Toplam: {{teklif.toplam}} · Geçerlilik: {{teklif.gecerlilik}}</p>\n${SIGNATURE}`,
        description: 'Teklif müşteri bağlantısını içerir. Yalnız teklif bağlantısı genel yayında ve çalışma alanında açıksa gönderilebilir.',
    },
    {
        key: 'aday-sekans-adimi',
        category: 'lead',
        name: 'Aday sekansı e-postası',
        subject: '{{adim.ad}}',
        bodyHtml: `<p>Merhaba {{aday.ad}},</p>\n<p>{{adim.metin}}</p>\n${SIGNATURE}`,
        description: 'Aday sekansındaki e-posta adımının metnini adaya gönderir.',
    },
    {
        key: 'destek-yaniti',
        category: 'ticket',
        name: 'Destek yanıtı',
        subject: 'Destek talebiniz #{{talep.no}}: {{talep.konu}}',
        bodyHtml: `<p>Sayın {{talep.sahibi}},</p>\n<p>#{{talep.no}} numaralı "{{talep.konu}}" konulu destek talebinize yanıtımız:</p>\n<blockquote>{{mesaj.metin}}</blockquote>\n${SIGNATURE}`,
        description: 'Talep sahibine görünür yanıtı e-posta ile iletir.',
    },
];
export const DEFAULT_EMAIL_TEMPLATE_BY_KEY = new Map(DEFAULT_EMAIL_TEMPLATES.map((t) => [t.key, t]));
/** Entegrasyonların varsayılan şablon anahtarları */
export const EMAIL_TEMPLATE_KEYS = {
    proposalSummary: 'teklif-gonderimi',
    proposalLink: 'teklif-baglantisi',
    sequenceStep: 'aday-sekans-adimi',
    ticketReply: 'destek-yaniti',
};
// ---------------------------------------------------------------------------------------------------------------------
// SMTP ayarı
// ---------------------------------------------------------------------------------------------------------------------
export const SMTP_HOST_RE = /^[A-Za-z0-9.:-]{1,253}$/;
export const smtpSettingsInputSchema = z
    .object({
    host: z
        .string()
        .trim()
        .min(1, 'sunucu gerekli')
        .max(253)
        .regex(SMTP_HOST_RE, 'Sunucu adı yalnız harf, rakam, nokta, tire ve iki nokta içerebilir'),
    port: z.number().int().min(1).max(65535),
    tlsMode: z.enum(EMAIL_TLS_MODES),
    username: z.string().trim().min(1).max(320).regex(NO_CONTROL).nullable(),
    /** Verilirse parola değişir; verilmezse mevcut parola korunur. Hiçbir yanıtta geri dönmez. */
    password: z.string().min(1).max(500).regex(NO_CONTROL).optional(),
    /** Kayıtlı parolayı sil (kimlik doğrulamasız sunucu) */
    clearPassword: z.boolean().optional(),
    fromName: emailDisplayNameSchema,
    fromAddress: emailAddressSchema,
    replyTo: emailAddressSchema.nullable(),
    hourlyLimit: z.number().int().min(1).max(1000),
    dailyLimit: z.number().int().min(1).max(10000),
    /** İyimser eşzamanlılık: ilk kayıtta null */
    version: z.number().int().min(1).nullable(),
})
    .strict()
    .refine((v) => v.dailyLimit >= v.hourlyLimit, {
    message: 'Günlük sınır saatlik sınırdan küçük olamaz',
    path: ['dailyLimit'],
})
    .refine((v) => !(v.clearPassword === true && v.password !== undefined), {
    message: 'Parola hem verilip hem silinemez',
    path: ['password'],
});
export const smtpLastTestSchema = z.object({
    at: z.string(),
    ok: z.boolean(),
    stage: z.enum(EMAIL_TEST_STAGES).nullable(),
    detail: z.string().nullable(),
});
export const smtpSettingsViewSchema = z.object({
    configured: z.boolean(),
    settings: z
        .object({
        host: z.string(),
        port: z.number().int(),
        tlsMode: z.enum(EMAIL_TLS_MODES),
        username: z.string().nullable(),
        passwordSet: z.boolean(),
        fromName: z.string(),
        fromAddress: z.string(),
        replyTo: z.string().nullable(),
        hourlyLimit: z.number().int(),
        dailyLimit: z.number().int(),
        enabled: z.boolean(),
        /** Mevcut ayar başarıyla test edildi (gönderim açılabilir) */
        verified: z.boolean(),
        lastTest: smtpLastTestSchema.nullable(),
        version: z.number().int(),
        updatedAt: z.string(),
    })
        .nullable(),
    usage: z.object({ lastHour: z.number().int(), lastDay: z.number().int() }),
});
export const smtpEnableInputSchema = z
    .object({ enabled: z.boolean(), version: z.number().int().min(1) })
    .strict();
export const smtpTestInputSchema = z.object({ to: emailAddressSchema }).strict();
export const smtpTestResultSchema = z.object({
    ok: z.boolean(),
    stage: z.enum(EMAIL_TEST_STAGES),
    errorClass: z.enum(EMAIL_ERROR_CLASSES).nullable(),
    detail: z.string(),
    /** Bağlantı TLS ile şifrelendi mi (null: bağlantı kurulamadı) */
    tls: z.boolean().nullable(),
    /** Kimlik doğrulama: kullanıldı / sunucu sunmadı / parola tanımsız */
    auth: z.enum(['used', 'not_offered', 'none']).nullable(),
    durationMs: z.number().int(),
});
export const emailAvailabilitySchema = z.object({
    available: z.boolean(),
    reason: z.enum(EMAIL_DISABLED_REASONS).nullable(),
    message: z.string(),
});
// ---------------------------------------------------------------------------------------------------------------------
// Şablonlar
// ---------------------------------------------------------------------------------------------------------------------
export const EMAIL_TEMPLATE_KEY_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;
export const EMAIL_TEMPLATE_BODY_MAX = 100_000;
export const emailTemplateInputSchema = z
    .object({
    /** Yeni özel şablonda boş bırakılırsa addan üretilir; varsayılanı düzenlerken varsayılanın anahtarı */
    key: z
        .string()
        .trim()
        .regex(EMAIL_TEMPLATE_KEY_RE, 'Anahtar küçük harf, rakam ve tire içerir (2–63)')
        .optional(),
    category: z.enum(EMAIL_TEMPLATE_CATEGORIES),
    name: z.string().trim().min(1, 'ad gerekli').max(120).regex(NO_CONTROL),
    subject: z.string().trim().min(1, 'konu gerekli').max(300).regex(NO_CONTROL),
    bodyHtml: z.string().trim().min(1, 'gövde gerekli').max(EMAIL_TEMPLATE_BODY_MAX),
    /** Güncellemede zorunlu (iyimser eşzamanlılık) */
    version: z.number().int().min(1).nullable().optional(),
})
    .strict();
export const emailTemplateViewSchema = z.object({
    key: z.string(),
    category: z.enum(EMAIL_TEMPLATE_CATEGORIES),
    name: z.string(),
    subject: z.string(),
    bodyHtml: z.string(),
    /** Kodda tanımlı varsayılan şablon */
    isDefault: z.boolean(),
    /** Varsayılan şablon bu çalışma alanında düzenlenmiş (satır var) */
    customized: z.boolean(),
    /** Gönderimi teklif müşteri bağlantısı gerektirir */
    requiresProposalLink: z.boolean(),
    variables: z.array(z.string()),
    version: z.number().int().nullable(),
    updatedAt: z.string().nullable(),
});
export const emailTemplateListSchema = z.object({ templates: z.array(emailTemplateViewSchema) });
export const emailPreviewInputSchema = z
    .object({
    templateKey: z.string().trim().regex(EMAIL_TEMPLATE_KEY_RE).optional(),
    draft: z
        .object({
        category: z.enum(EMAIL_TEMPLATE_CATEGORIES),
        subject: z.string().max(300),
        bodyHtml: z.string().max(EMAIL_TEMPLATE_BODY_MAX),
    })
        .strict()
        .optional(),
    /** Örnek kayıt (okuma yetkisi denetlenir); verilmezse örnek değerler kullanılır */
    recordId: uuid.optional(),
})
    .strict()
    .refine((v) => (v.templateKey === undefined) !== (v.draft === undefined), {
    message: 'Şablon anahtarı ya da taslak verilmeli (ikisi birden değil)',
});
export const emailPreviewSchema = z.object({
    subject: z.string(),
    html: z.string(),
    text: z.string(),
    /** Kayıt verilmediği için örnek değerler kullanıldı */
    usedSample: z.boolean(),
    unknownVariables: z.array(z.string()),
    notAllowedVariables: z.array(z.string()),
    /** Bu kayıtla değeri çözülemeyen / gönderimde akışın vermesi gereken anahtar kelimeler */
    missingVariables: z.array(z.string()),
    /** Temizlemede kaldırılan içerik türleri (ör. script, olay niteliği) */
    removed: z.array(z.string()),
});
// ---------------------------------------------------------------------------------------------------------------------
// Gönderim kaydı
// ---------------------------------------------------------------------------------------------------------------------
export const emailSendListQuerySchema = z
    .object({
    status: z.enum(EMAIL_SEND_STATUSES).optional(),
    source: z.enum(EMAIL_SEND_SOURCES).optional(),
    /** Alıcı ya da konu içinde arama */
    q: z.string().trim().max(120).optional(),
    before: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    .strict();
export const emailSendListItemSchema = z.object({
    id: z.string(),
    createdAt: z.string(),
    status: z.enum(EMAIL_SEND_STATUSES),
    source: z.enum(EMAIL_SEND_SOURCES),
    to: z.array(z.object({ address: z.string(), name: z.string().nullable() })),
    /** Kopya (Cc) alıcıları — Kime'den AYRI tutulur; Bcc yoktur (M6-H1) */
    cc: z.array(z.object({ address: z.string(), name: z.string().nullable() })),
    /** SMTP sunucusunun reddettiği alıcı adresleri (Kime ya da Cc); kısmi rette ileti diğer alıcılara gitmiştir */
    rejected: z.array(z.string()),
    subject: z.string(),
    templateKey: z.string().nullable(),
    recordType: z.string().nullable(),
    recordId: z.string().nullable(),
    actorName: z.string().nullable(),
    attempts: z.number().int(),
    acceptedAt: z.string().nullable(),
    errorClass: z.enum(EMAIL_ERROR_CLASSES).nullable(),
    /** Belirsiz sonuç elle uzlaştırıldı */
    reconciled: z.boolean(),
    /** Sabitlenmiş ek (teklif PDF'i); yoksa null */
    attachment: emailAttachmentSchema.nullable(),
    /** "Yeniden dene" kullanılabilir (yalnız başarısız) */
    canRetry: z.boolean(),
    /** Uzlaştırma kullanılabilir (yalnız sonucu belirsiz) */
    canReconcile: z.boolean(),
});
export const emailSendListSchema = z.object({
    items: z.array(emailSendListItemSchema),
    /** Sonraki sayfa için `before` değeri */
    nextBefore: z.string().nullable(),
});
export const emailSendDetailSchema = emailSendListItemSchema.extend({
    fromName: z.string(),
    fromAddress: z.string(),
    replyTo: z.string().nullable(),
    messageId: z.string(),
    /** Gizli bağlantılar maskelenmiş gövde */
    bodyHtml: z.string(),
    bodyText: z.string(),
    hasSecretParts: z.boolean(),
    lastAttemptAt: z.string().nullable(),
    smtpResponse: z.string().nullable(),
    errorDetail: z.string().nullable(),
    quarantineReason: z.string().nullable(),
    reconcile: z
        .object({
        at: z.string(),
        byName: z.string().nullable(),
        outcome: z.enum(['accepted', 'resend']),
        note: z.string().nullable(),
    })
        .nullable(),
});
export const emailReconcileInputSchema = z
    .object({
    /** `accepted`: SMTP günlüğü/alıcıyla doğrulandı, gönderilmiş say · `resend`: ulaşmadı, yeniden gönder (çift ileti riski) */
    outcome: z.enum(['accepted', 'resend']),
    note: z.string().trim().min(3, 'gerekçe gerekli').max(1000),
})
    .strict();
/** Entegrasyon uçlarının (teklif, sekans, destek) e-posta sonucu */
export const emailEnqueueOutcomeSchema = z.object({
    status: z.enum(['queued', 'disabled']),
    sendId: z.string().nullable(),
    reason: z.enum(EMAIL_DISABLED_REASONS).nullable(),
    message: z.string(),
});
/** Kayıt görünümlerindeki (destek mesajı, sekans adımı) e-posta durumu */
export const emailStatusRefSchema = z.object({
    sendId: z.string(),
    status: z.enum(EMAIL_SEND_STATUSES),
});
// ---------------------------------------------------------------------------------------------------------------------
// Teklif e-postası (M3 teklif gönderimi entegrasyonu)
// ---------------------------------------------------------------------------------------------------------------------
export const proposalEmailInputSchema = z
    .object({
    to: z.array(emailRecipientSchema).min(1, 'alıcı gerekli').max(PROPOSAL_EMAIL_MAX_RECIPIENTS),
    /** Kopya (Cc) alıcıları (M6-H1). Kime + Cc toplamı (tekilleştirme sonrası) teklif sınırını aşamaz; Bcc yoktur. */
    cc: z.array(emailRecipientSchema).max(PROPOSAL_EMAIL_MAX_RECIPIENTS).default([]),
    templateKey: z.string().trim().regex(EMAIL_TEMPLATE_KEY_RE),
    /** Çift tıklama / yeniden deneme: aynı belirteç ikinci e-posta üretmez */
    clientToken: uuid,
    /**
     * Final revizyonun PDF'i ek olarak gönderilsin mi. API'de açıkça istenir (varsayılan `false`: eski/otomatik çağıranlar
     * habersiz ek üretmez); arayüz, PDF hazır ve sınır içindeyse kutuyu işaretli açar.
     */
    attachPdf: z.boolean().default(false),
})
    .strict()
    .refine((v) => normalizeEmailRecipients(v.to, v.cc).total <= PROPOSAL_EMAIL_MAX_RECIPIENTS, {
    message: `Kime ve Cc toplamı en çok ${String(PROPOSAL_EMAIL_MAX_RECIPIENTS)} alıcı olabilir`,
    path: ['cc'],
});
/** Teklif PDF ekinin kullanılabilirliği (e-posta seçenekleri) */
export const PROPOSAL_ATTACHMENT_STATUSES = [
    'ready',
    'pending',
    'failed',
    'missing',
    'too_large',
    'not_final',
];
export const proposalEmailOptionsSchema = z.object({
    availability: emailAvailabilitySchema,
    /** Teklif e-postayla gönderilebilir durumda mı (final) */
    sendable: z.boolean(),
    sendableReason: z.string().nullable(),
    /** Teklif müşteri bağlantısı üretilebilir mi (genel yayın + çalışma alanı ayarı + yazma yetkisi) */
    link: z.object({ available: z.boolean(), reason: z.string().nullable() }),
    /** Final revizyonun PDF'i ek olarak gönderilebilir mi (hazır, sınır içinde); değilse gerekçe */
    attachment: z.object({
        available: z.boolean(),
        status: z.enum(PROPOSAL_ATTACHMENT_STATUSES),
        reason: z.string().nullable(),
        revisionNo: z.number().int().nullable(),
        fileName: z.string().nullable(),
        byteSize: z.number().int().nullable(),
        maxBytes: z.number().int(),
    }),
    suggestedRecipients: z.array(z.object({ address: z.string(), name: z.string().nullable() })),
    templates: z.array(z.object({ key: z.string(), name: z.string(), requiresProposalLink: z.boolean() })),
    recent: z.array(z.object({
        sendId: z.string(),
        createdAt: z.string(),
        status: z.enum(EMAIL_SEND_STATUSES),
        to: z.array(z.string()),
        /** Kopya (Cc) adresleri */
        cc: z.array(z.string()),
        /** Gönderime sabitlenmiş PDF eki (dosya adı + revizyon); yoksa null */
        attachment: z.object({ fileName: z.string(), revisionNo: z.number().int() }).nullable(),
    })),
});
/** Akıllı bağlantı çözümü (uygulama içi kayıt bağlantısı) */
export const smartLinkResolveSchema = z.object({ recordType: z.string(), recordId: z.string() });
//# sourceMappingURL=email.js.map